/**
 * Report Print Route (PDF rebuild — headless-Chrome rendering source)
 *
 * URL: /reports/:reportId/print?token=<print-token>
 *
 * A sibling of `portal.$token.tsx` — NOT under `_authenticated`. The short-lived
 * print token IS the credential; no auth state is consulted. The server's
 * renderer (`renderPdfViaBrowser`) navigates a headless Chrome here, this page
 * fetches the render payload from the pre-auth `print-data` endpoint, renders it
 * with the SAME `ReportCanvas`/`WidgetRenderer` the editor + preview use, then
 * signals `window.__PRINT_READY__` so the renderer prints the page to PDF.
 *
 * This is the WHOLE point of the rebuild: the PDF IS the web render — one
 * rendering stack, no react-pdf, no parity tax. Pagination + per-page layout
 * mirror `ReportPreviewModal` exactly (split by height → `ReportCanvas` per page)
 * so the PDF matches what the user previews.
 *
 * NO app chrome (no sidebar/nav) — minimal surface, print-optimized CSS.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ReportCanvas } from "../components/reports/ReportCanvas";
import { splitWidgetsIntoPages } from "../lib/gridUtils";
import {
  getGridDimensions,
  MARGIN_TOP_PT,
  MARGIN_BOTTOM_PT,
  MARGIN_SIDES_PT,
  SLIDE_MARGIN_PT,
  SLIDE_PDF_WIDTH_PT,
  SLIDE_PDF_HEIGHT_PT,
} from "@provenleap/types/report-rendering";
import type { ReportPrintPayload } from "@provenleap/types";
import type { WidgetConfig } from "../lib/templateUtils";
import type { WidgetData } from "../lib/widgetData";

declare global {
  interface Window {
    /** Set true once the report has rendered + settled — the renderer waits on this. */
    __PRINT_READY__?: boolean;
    /** Set true on a load/render failure — the renderer aborts (no PDF of an error page). */
    __PRINT_ERROR__?: boolean;
    /** Report format — the renderer reads this to pick the page.pdf() page size. */
    __PRINT_FORMAT__?: "document" | "slide-deck";
    /** Per-page header HTML (page.pdf headerTemplate) — brand + title + period. */
    __PRINT_HEADER__?: string;
    /** Per-page footer HTML (page.pdf footerTemplate) — footerText/Powered-by + page #. */
    __PRINT_FOOTER__?: string;
  }
}

export const Route = createFileRoute("/reports/$reportId/print")({
  component: PrintPage,
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
});

/** Settle delay (ms) — a FLOOR after fonts + images load, so Recharts' one-shot
 *  mount animation (≈1500ms) finishes before page.pdf() captures the final SVG. */
const RENDER_SETTLE_MS = 1800;

/** Per-image load cap (ms) — one stuck image cannot block the whole render. */
const IMAGE_LOAD_CAP_MS = 8000;

function markError() {
  if (typeof window !== "undefined") window.__PRINT_ERROR__ = true;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Builds the page.pdf header + footer templates (Chrome repeats them on every
 * page, in the page margins, and resolves <span class="pageNumber/totalPages">).
 * Restores the per-page chrome the old react-pdf renderer drew: a header with the
 * brand logo-or-name + report title + reporting period + primary-color rule, and
 * a footer with the white-label footerText OR "Powered by ProvenLeap" + page #.
 * All tenant strings are escaped (they land in header HTML rendered by Chrome).
 */
function buildHeaderFooter(payload: ReportPrintPayload): {
  header: string;
  footer: string;
} {
  const b = payload.branding;
  const primary =
    b?.primaryColor && /^#[0-9a-fA-F]{3,8}$/.test(b.primaryColor)
      ? b.primaryColor
      : "#2563eb";
  const brandLeft = b?.logoUrl
    ? `<img src="${escapeHtml(b.logoUrl)}" style="height:16px;max-width:150px;object-fit:contain;" />`
    : `<span style="font-weight:600;color:#0f172a;">${escapeHtml(b?.companyName || "ProvenLeap")}</span>`;
  const range =
    payload.dateRange.start && payload.dateRange.end
      ? `${payload.dateRange.start} – ${payload.dateRange.end}`
      : "";
  const header = `<div style="font-size:9px;width:100%;box-sizing:border-box;padding:2px 0.5in 0;color:#475569;-webkit-print-color-adjust:exact;">
    <div style="display:flex;align-items:flex-end;justify-content:space-between;border-bottom:2px solid ${primary};padding-bottom:3px;">
      <div style="flex:1;text-align:left;">${brandLeft}</div>
      <div style="flex:2;text-align:center;font-weight:600;color:#0f172a;">${escapeHtml(payload.reportName)}</div>
      <div style="flex:1;text-align:right;color:#64748b;">${escapeHtml(range)}</div>
    </div>
  </div>`;
  // Free tier (no white-label) → "Powered by ProvenLeap"; white-label → the
  // agency footerText, or nothing if they opted out of the powered-by line.
  const footerLeft = b
    ? b.footerText
      ? escapeHtml(b.footerText)
      : b.poweredByProvenLeap
        ? "Powered by ProvenLeap"
        : ""
    : "Powered by ProvenLeap";
  const footer = `<div style="font-size:8px;width:100%;box-sizing:border-box;padding:0 0.5in 2px;color:#94a3b8;-webkit-print-color-adjust:exact;">
    <div style="display:flex;align-items:center;justify-content:space-between;">
      <span>${footerLeft}</span>
      <span>Page <span class="pageNumber"></span> of <span class="totalPages"></span></span>
    </div>
  </div>`;
  return { header, footer };
}

function PrintPage() {
  const { token } = Route.useSearch();
  const [payload, setPayload] = useState<ReportPrintPayload | null>(null);
  const [failed, setFailed] = useState(false);

  // 1. Fetch the render payload by the print token (same-origin /api, like the
  //    portal — Vite-proxied in dev, colocated worker in prod).
  useEffect(() => {
    if (!token) {
      setFailed(true);
      markError();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/reports/print-data/${encodeURIComponent(token)}`,
          { method: "GET" },
        );
        if (!res.ok) throw new Error(`print-data ${res.status}`);
        const data = (await res.json()) as ReportPrintPayload;
        if (!cancelled) setPayload(data);
      } catch {
        if (!cancelled) {
          setFailed(true);
          markError();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // 2. Flatten the payload's pages → widgets, then re-split by height (mirrors
  //    ReportPreviewModal so the PDF matches the preview).
  const format = payload?.format ?? "document";
  const dims = useMemo(() => getGridDimensions(format), [format]);
  const widgets: WidgetConfig[] = useMemo(() => {
    if (!payload) return [];
    return payload.pages.flatMap((p) =>
      p.widgets.map((w) => ({
        id: w.id,
        type: w.type,
        title: w.title,
        position: w.position,
        config: w.config,
      })),
    );
  }, [payload]);
  const widgetData: Record<string, WidgetData> = useMemo(() => {
    const map: Record<string, WidgetData> = {};
    if (payload) {
      for (const p of payload.pages) {
        for (const w of p.widgets) map[w.id] = w.data as WidgetData;
      }
    }
    return map;
  }, [payload]);
  const printPages = useMemo(
    () =>
      splitWidgetsIntoPages(
        widgets,
        dims.contentHeightPx,
        dims.rowHeightPx,
        dims.gapPx,
      ),
    [widgets, dims.contentHeightPx, dims.rowHeightPx, dims.gapPx],
  );

  // 3. Signal readiness after fonts + IMAGES load + a settle FLOOR (charts).
  //    The fixed settle alone is NOT enough — logos/cover/thumbnails load <img>
  //    async after React renders, so a slow image origin would otherwise be
  //    captured blank. Also publish the page size + header/footer the renderer
  //    reads via page.evaluate.
  useEffect(() => {
    if (!payload) return;
    window.__PRINT_FORMAT__ = payload.format;
    const hf = buildHeaderFooter(payload);
    window.__PRINT_HEADER__ = hf.header;
    window.__PRINT_FOOTER__ = hf.footer;

    let cancelled = false;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const waitForImages = () =>
      Promise.all(
        Array.from(document.images).map((img) =>
          img.complete
            ? Promise.resolve()
            : new Promise<void>((resolve) => {
                const done = () => resolve();
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
                // Per-image cap: a stuck image cannot block the render forever.
                window.setTimeout(done, IMAGE_LOAD_CAP_MS);
              }),
        ),
      );

    const fonts = (
      document as Document & { fonts?: { ready?: Promise<unknown> } }
    ).fonts;
    const fontsReady = fonts?.ready ?? Promise.resolve();

    // Give widgets a frame to mount their <img>s, then wait for fonts + images,
    // then a settle floor for chart animation, then signal ready.
    const raf = requestAnimationFrame(() => {
      Promise.all([fontsReady, waitForImages()]).then(() => {
        if (cancelled) return;
        settleTimer = setTimeout(() => {
          if (!cancelled) window.__PRINT_READY__ = true;
        }, RENDER_SETTLE_MS);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (settleTimer) clearTimeout(settleTimer);
    };
  }, [payload]);

  if (failed) {
    return (
      <div data-print-error style={{ padding: 24, fontFamily: "sans-serif" }}>
        Failed to load report for printing.
      </div>
    );
  }
  if (!payload) {
    return (
      <div style={{ padding: 24, fontFamily: "sans-serif" }}>Preparing…</div>
    );
  }

  // @page size + margins per format (content px == printable area at 96dpi).
  const pageCss =
    format === "slide-deck"
      ? `@page { size: ${SLIDE_PDF_WIDTH_PT}pt ${SLIDE_PDF_HEIGHT_PT}pt; margin: ${SLIDE_MARGIN_PT}pt; }`
      : `@page { size: A4; margin: ${MARGIN_TOP_PT}pt ${MARGIN_SIDES_PT}pt ${MARGIN_BOTTOM_PT}pt ${MARGIN_SIDES_PT}pt; }`;

  const pages = printPages.length > 0 ? printPages : [[]];

  return (
    <div className="print-root" data-testid="report-print-root">
      <style>{`
        ${pageCss}
        html, body, #root { margin: 0; padding: 0; background: #ffffff; }
        .print-root { background: #ffffff; }
        .print-page {
          width: ${dims.contentWidthPx}px;
          position: relative;
          background: #ffffff;
          break-after: page;
          page-break-after: always;
          overflow: hidden;
        }
        .print-page:last-child { break-after: auto; page-break-after: auto; }
        /* Strip ReportCanvas's editor "paper" chrome — its inline width is
           contentWidth+96px plus 48px padding + a card frame, which is ~96px
           wider than the printable area and clips the right edge. Override to the
           exact content width (no padding/frame) so the report fills the page. */
        [data-testid="report-canvas-wrapper"] {
          width: ${dims.contentWidthPx}px !important;
          padding: 0 !important;
          margin: 0 !important;
          border: 0 !important;
          box-shadow: none !important;
          border-radius: 0 !important;
        }
        /* Print fidelity: keep chart/widget backgrounds, kill hover affordances. */
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        /* Hide dev-only devtools overlays so they never leak into the PDF (they
           don't exist in a production build, but a local dev render would catch them). */
        .TanStackRouterDevtools, [class*="TanStackRouterDevtools"],
        [class*="tsqd"], [data-tanstack-router-devtools],
        .tsr-dev-tools, #__tanstack-router-devtools { display: none !important; }
      `}</style>
      {pages.map((pageWidgets, i) => (
        <div
          className="print-page"
          key={i}
          style={{ width: dims.contentWidthPx }}
          data-testid={`print-page-${i}`}
        >
          <ReportCanvas
            widgets={pageWidgets}
            widgetData={widgetData}
            editable={false}
            format={format}
            reportDateRangeStart={payload.dateRange.start ?? undefined}
            reportDateRangeEnd={payload.dateRange.end ?? undefined}
            selectedClientName={payload.client.name ?? undefined}
            selectedClientLogoUrl={payload.client.logoUrl ?? undefined}
          />
        </div>
      ))}
    </div>
  );
}
